import React, { useCallback, useEffect, useRef } from "react";
import { Input } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Box from "./box";
import { IconBuilder } from "../../icons/IconBuilder";

/**
 * Renders a Hero UI search input component.
 * Props match the existing Search component so it can be swapped in easily.
 */
export default function HeroSearch({
  setSearchString,
  allowClear = false,
  box = true,
  className,
}: {
  setSearchString: (searchString: string) => void;
  allowClear?: boolean;
  box?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("search") || "";
  const baseRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSearchString(query);
  }, [query, setSearchString]);

  const searchStringHandler = useCallback(
    (value: string | undefined) => {
      const searchValue = value ?? "";

      setSearchString(searchValue);
      const newParams: { [key: string]: string } = {};

      const currentPageSize = searchParams.get("pageSize");
      if (currentPageSize) {
        newParams.pageSize = currentPageSize;
      }

      const currentPage = searchParams.get("page");
      if (searchValue !== query && currentPage !== null) {
        newParams.page = "1";
      } else if (currentPage) {
        newParams.page = currentPage;
      }

      if (searchValue) {
        newParams.search = searchValue;
      }

      setSearchParams(newParams);
    },
    [setSearchParams, setSearchString, searchParams, query],
  );

  // Attach Enter key handler to the underlying input inside the HeroInput
  useEffect(() => {
    const root = baseRef.current;
    if (!root) return null;
    const inputEl = root.querySelector("input");
    if (!inputEl) return null;
    /**
     * Handles the keydown event on the input element and triggers the search handler when Enter is pressed.
     * @param {KeyboardEvent} e - The keyboard event.
     * @returns {void}
     */
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        // Use the input's current value
        const val = (e.target as HTMLInputElement).value;
        searchStringHandler(val);
      }
    };

    inputEl.addEventListener("keydown", onKeyDown);
    return () => inputEl.removeEventListener("keydown", onKeyDown);
  }, [baseRef, searchStringHandler]);

  const handleClear = useCallback(() => {
    searchStringHandler("");
  }, [searchStringHandler]);

  const searchInput = (
    <Input
      startContent={<IconBuilder icon="Search" size={20} color="#A1A1AA" />}
      aria-label={t("translation:search")}
      placeholder={t("translation:search")}
      defaultValue={query}
      isClearable={allowClear}
      size="md"
      onClear={handleClear}
      baseRef={baseRef}
      className={className}
    />
  );

  return box ? <Box>{searchInput}</Box> : searchInput;
}
